<script>
import BanobagiTransactionList from "./BanobagiTransactionList.vue";
import BanobagiTransactionSummary from "./BanobagiTransactionSummary.vue";
import {
  loadBanobagiRightsLedger,
  TRANSACTION_LIST_ENDPOINTS
} from "@/services/banobagiRightsService";
import { BANOBAGI_RIGHT_DEFINITIONS } from "@/utils/banobagiRights";

export default {
  name: "BanobagiTransactionDialogContent",
  components: {
    BanobagiTransactionList,
    BanobagiTransactionSummary
  },
  props: {
    isBottomSheet: {
      type: Boolean,
      default: false
    }
  },
  emits: ["close", "action"],
  data() {
    return {
      activeTab: "all",
      isLoading: true,
      errorMessage: "",
      transactions: [],
      summaryTotal: 0,
      filter: {
        month_ago: 0,
        all_flag: true,
        page: 1,
        pageSize: 99999
      }
    };
  },
  computed: {
    tabs() {
      return [
        { key: "all", label: this.$t("all") },
        ...BANOBAGI_RIGHT_DEFINITIONS.map(({ key, label }) => ({ key, label }))
      ];
    },
    filteredTransactions() {
      if (this.activeTab === "all") return this.transactions;
      return this.transactions.filter(item =>
        item._rights.some(right => right.key === this.activeTab)
      );
    }
  },
  created() {
    this.loadTransactions();
  },
  methods: {
    async loadTransactions() {
      this.isLoading = true;
      this.errorMessage = "";

      try {
        const ledger = await loadBanobagiRightsLedger(
          this.$axios,
          this.filter
        );

        this.transactions = ledger.transactions.map((item, index) =>
          this.normalizeTransaction(item, index)
        );
        this.summaryTotal = Object.values(ledger.summary).reduce(
          (total, value) => total + Number(value || 0),
          0
        );
      } catch (error) {
        this.transactions = [];
        this.summaryTotal = 0;
        this.errorMessage = this.$t("error_message");
        this.logError("load banobagi transactions", error, {
          url: TRANSACTION_LIST_ENDPOINTS.join(", "),
          methods: "POST",
          data: JSON.stringify(this.filter)
        });
      } finally {
        this.isLoading = false;
      }

      const firstTransaction = this.filteredTransactions[0];
      if (firstTransaction) this.openTransaction(firstTransaction);
    },
    normalizeTransaction(item, index) {
      const transactionId = item.transaction_id ?? item.id ?? null;
      const identity = this.normalizeIdentity(transactionId ?? "row-" + index);
      const rights = Array.isArray(item._rights) ? item._rights : [];

      return {
        ...item,
        _key: identity + "-" + index,
        _transactionId: transactionId,
        _categoryName: this.resolveCategoryName(item, rights),
        _rightsDelta: rights.length
          ? rights.reduce((total, right) => total + Number(right.value || 0), 0)
          : null,
        _formattedDate: this.formatTransactionDate(item.transaction_date),
        _branchName:
          item.branch_name ||
          item.sales_channel_name ||
          item.channel_name ||
          "-",
        _invoiceNo: item.invoice_no || item.invoice_no_ref || "",
        _isExpanded: false,
        _detail: this.normalizeDetail({}, item)
      };
    },
    normalizeIdentity(value) {
      return (
        String(value)
          .trim()
          .replace(/[^a-zA-Z0-9_-]+/g, "-")
          .replace(/^-+|-+$/g, "") || "transaction"
      );
    },
    resolveCategoryName(item, rights) {
      if (rights.length > 0) {
        return rights.map(right => right.label).join(", ");
      }

      return (
        item.fan_type_name ||
        item.category_name ||
        item.transaction_type_name ||
        item.campaign_name ||
        item.promotion_name ||
        item.tier_name ||
        this.$t("transaction_title")
      );
    },
    formatTransactionDate(value) {
      if (!value) return "-";
      const date = this.$moment(value);
      if (!date || (date.isValid && !date.isValid())) return "-";
      return date.format("DD MMM YY (HH:mm)");
    },
    selectTab(key) {
      this.activeTab = key;
    },
    moveTabFocus(event, index) {
      const nextIndex = (index + this.tabs.length) % this.tabs.length;
      this.selectTab(this.tabs[nextIndex].key);
      const tabButtons =
        event.currentTarget.parentElement?.querySelectorAll('[role="tab"]');
      this.$nextTick(() => tabButtons?.[nextIndex]?.focus());
    },
    openTransaction(item) {
      if (!item._isExpanded) item._isExpanded = true;
    },
    toggleTransaction(item) {
      if (item._isExpanded) {
        item._isExpanded = false;
        return;
      }

      this.openTransaction(item);
    },
    normalizeDetail(detail, item) {
      const transactionDetails = Array.isArray(detail.transaction_details)
        ? detail.transaction_details
        : Array.isArray(item.product_list)
        ? item.product_list
        : [];
      const products = transactionDetails.map((product, index) => ({
        _key:
          this.normalizeIdentity(
            product.product_id ||
              product.id ||
              product.product_name ||
              product.barcode ||
              "product"
          ) +
          "-" +
          index,
        name:
          product.product_name ||
          product.name ||
          product.barcode ||
          String(product.product_id || "-"),
        quantity:
          Number(product.quantity) ||
          (transactionDetails.length === 1
            ? Number(item.total_item) || 1
            : 1),
        total:
          Number(
            product.after_discount_price ??
              product.total_price ??
              product.price ??
              0
          ) || 0
      }));
      const grandTotal = Number(
        detail.grand_total ?? item.total_spend ?? item.grand_total ?? 0
      );

      return {
        products,
        grandTotal: Number.isFinite(grandTotal) ? grandTotal : 0
      };
    },
    logError(action, error, config) {
      this.$logger({
        response: error?.response || { config },
        action: {
          action,
          id: 3,
          note: error?.message || "Unknown error"
        }
      });
    }
  }
};
</script>

<template>
  <section
    class="banobagi-transaction-dialog"
    data-test="transaction-modal"
    :aria-label="$t('transaction_header')"
  >
    <button
      type="button"
      class="banobagi-transaction-dialog__close"
      data-test="transaction-modal-close-btn"
      :aria-label="$t('close_window')"
      @click="$emit('close')"
    >
      <font-awesome-icon :icon="['fas', 'times']" aria-hidden="true" />
    </button>

    <BanobagiTransactionSummary :total="summaryTotal" />

    <div
      class="banobagi-transaction-dialog__tabs"
      role="tablist"
      :aria-label="$t('transaction_header')"
      data-test="transaction-filter-tabs"
    >
      <button
        v-for="(tab, index) in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        :class="[
          'banobagi-transaction-dialog__tab',
          { 'is-active': activeTab === tab.key }
        ]"
        :data-test="'transaction-filter-' + tab.key + '-tab'"
        :aria-selected="activeTab === tab.key"
        :tabindex="activeTab === tab.key ? 0 : -1"
        aria-controls="banobagi-transaction-list-panel"
        @click="selectTab(tab.key)"
        @keydown.arrow-left.prevent="moveTabFocus($event, index - 1)"
        @keydown.arrow-right.prevent="moveTabFocus($event, index + 1)"
        @keydown.home.prevent="moveTabFocus($event, 0)"
        @keydown.end.prevent="moveTabFocus($event, tabs.length - 1)"
      >
        <span>{{ tab.label }}</span>
        <strong v-if="tab.key === 'all'">
          {{ numeralFormat(summaryTotal) }}
        </strong>
      </button>
    </div>

    <div
      id="banobagi-transaction-list-panel"
      class="banobagi-transaction-dialog__body"
      role="tabpanel"
      data-test="transaction-list-panel"
    >
      <div
        v-if="isLoading"
        class="banobagi-transaction-dialog__state"
        role="status"
        data-test="transaction-loading-msg"
      >
        {{ $t("loading") }}
      </div>

      <div
        v-else-if="errorMessage"
        class="banobagi-transaction-dialog__state"
        role="alert"
        data-test="transaction-error-msg"
      >
        <span>{{ errorMessage }}</span>
        <button
          type="button"
          class="banobagi-transaction-dialog__retry"
          data-test="transaction-retry-btn"
          @click="loadTransactions"
        >
          {{ $t("retry") }}
        </button>
      </div>

      <BanobagiTransactionList
        v-else
        :transactions="filteredTransactions"
        @toggle="toggleTransaction"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.banobagi-transaction-dialog {
  position: relative;
  display: flex;
  width: 100%;
  height: min(
    632px,
    calc(
      100dvh - 176px - env(safe-area-inset-top) - env(safe-area-inset-bottom)
    )
  );
  min-height: 440px;
  flex-direction: column;
  overflow: hidden;

  color: var(--text-color);
}

.banobagi-transaction-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--secondary-color);
  cursor: pointer;
  font: inherit;
  font-size: var(--font-body-lg);
}

.banobagi-transaction-dialog__close::before {
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--text-color);
  content: "";
}

.banobagi-transaction-dialog__close svg {
  position: relative;
  z-index: 1;
}

.banobagi-transaction-dialog__close:focus-visible,
.banobagi-transaction-dialog__tab:focus-visible,
.banobagi-transaction-dialog__retry:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 3px;
}

.banobagi-transaction-dialog__tabs {
  display: flex;
  min-height: 56px;
  flex: 0 0 auto;
  align-items: stretch;
  overflow-x: auto;
  border-bottom: 1px solid var(--disable-color);
  scrollbar-width: none;
}

.banobagi-transaction-dialog__tabs::-webkit-scrollbar {
  display: none;
}

.banobagi-transaction-dialog__tab {
  position: relative;
  display: inline-flex;
  min-width: max-content;
  min-height: 44px;
  flex: 1 0 auto;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  border: 0;
  background: transparent;
  color: var(--crm-link-color, #8d8d8d);
  cursor: pointer;
  font: inherit;
  font-size: var(--font-body);
  line-height: 1.35;
  white-space: nowrap;
}

.banobagi-transaction-dialog__tab::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 3px;
  background: transparent;
  content: "";
}

.banobagi-transaction-dialog__tab strong {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.banobagi-transaction-dialog__tab.is-active {
  color: var(--text-color);
  font-weight: 600;
}

.banobagi-transaction-dialog__tab.is-active::after {
  background: var(--primary-color);
}

.banobagi-transaction-dialog__body {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: var(--disable-color) transparent;
  scrollbar-width: thin;
}

.banobagi-transaction-dialog__state {
  display: flex;
  min-height: 160px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  color: var(--crm-link-color, #8d8d8d);
  font-size: var(--font-body);
  line-height: 1.5;
  text-align: center;
}

.banobagi-transaction-dialog__retry {
  min-width: 96px;
  min-height: 44px;
  padding: 8px 16px;
  border: 1px solid var(--primary-color);
  border-radius: 999px;
  background: transparent;
  color: var(--primary-color);
  cursor: pointer;
  font: inherit;
  font-size: var(--font-body);
  font-weight: 600;
}

@media (max-width: 360px) {
  .banobagi-transaction-dialog {
    min-height: 400px;
    border-radius: 24px;
  }

  .banobagi-transaction-dialog__tab {
    padding-right: 12px;
    padding-left: 12px;
    font-size: var(--font-body-sm);
  }
}
</style>
